import {useState, useEffect} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@sakura-grp/ui/drawer";
import {Button} from "@sakura-grp/ui/button";
import {Input} from "@sakura-grp/ui/input";
import {Label} from "@sakura-grp/ui/label";
import {toast} from "@sakura-grp/ui/sonner";
import {updateCustomerName} from "@/lib/api/customers";
import {useDirtyDialogGuard} from "@/hooks/use-dirty-dialog-guard";
import {useKeyboardInset} from "@/hooks/useKeyboardInset";
import {detectMojibakeKind, suggestKanji} from "@sakura-grp/lib";

function MojibakeSuggestions({value, onSelect}: {value: string; onSelect: (next: string) => void}) {
  if (!value || !detectMojibakeKind(value)) return null;
  const suggestions = suggestKanji(value);
  if (suggestions.length === 0) return null;
  return (
    <div className="mt-sm flex flex-wrap items-center gap-sm">
      <span className="text-muted-foreground text-xs">💡 文字化け修正候補:</span>
      {suggestions.map((s) => (
        <Button
          key={s}
          type="button"
          variant="outline"
          className="h-auto px-md py-sm text-base font-semibold"
          onClick={() => onSelect(s)}
        >
          {s}
        </Button>
      ))}
    </div>
  );
}

interface NameEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  currentName: string;
  currentKana: string;
}

export function NameEditSheet({
  open,
  onOpenChange,
  customerId,
  currentName,
  currentKana,
}: NameEditSheetProps) {
  const [name, setName] = useState(currentName);
  const [kana, setKana] = useState(currentKana);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setName(currentName);
      setKana(currentKana);
    }
  }, [open, currentName, currentKana]);

  const mutation = useMutation({
    mutationFn: () => updateCustomerName(customerId, name, kana),
    onSuccess: () => {
      toast.success("氏名を更新しました");
      queryClient.invalidateQueries({queryKey: ["customer-detail", customerId]});
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const hasChanges = name !== currentName || kana !== currentKana;
  const canSave = hasChanges && name.trim().length > 0;

  const {DirtyConfirmDialog, requestClose} = useDirtyDialogGuard({
    isDirty: hasChanges,
    onClose: () => onOpenChange(false),
  });

  // vaul の repositionInputs は iOS で visualViewport.offsetTop 非考慮の
  // 二重シフトを起こすため無効化し、自前の inset でフッターをキーボード直上に
  // 密着させる (vaul#620 / docs/design/20260611-bizcard-drawer-keyboard-inset.md)
  const keyboardInset = useKeyboardInset();

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        dismissible={!hasChanges}
        repositionInputs={false}
      >
        <DrawerContent
          className="px-lg pb-safe-min"
          style={keyboardInset > 0 ? {paddingBottom: keyboardInset} : undefined}
        >
          <DrawerHeader className="pb-md">
            <DrawerTitle>顧客情報の編集</DrawerTitle>
            <DrawerDescription>正しい内容に訂正してください</DrawerDescription>
          </DrawerHeader>

          <div className="space-y-md min-h-0 flex-1 overflow-y-auto">
            <div className="space-y-sm">
              <Label htmlFor="customer-name">氏名</Label>
              <Input
                id="customer-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="山田 太郎"
                className="text-base"
              />
              <MojibakeSuggestions value={name} onSelect={setName} />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="customer-kana">フリガナ</Label>
              <Input
                id="customer-kana"
                value={kana}
                onChange={(e) => setKana(e.target.value)}
                placeholder="ヤマダ タロウ"
                className="text-base"
              />
            </div>
          </div>

          <DrawerFooter className="p-0 pt-md flex shrink-0 flex-col gap-md">
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !canSave}
            >
              {mutation.isPending ? "保存中..." : "保存する"}
            </Button>
            <Button
              variant="outline"
              onClick={requestClose}
              disabled={mutation.isPending}
            >
              やめる
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <DirtyConfirmDialog />
    </>
  );
}
